// Modal component for displaying transfers funnel analysis
// Shows comprehensive funnel data with step-by-step breakdown and visualizations

import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  Box,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  Badge,
  useColorModeValue,
  SimpleGrid,
  Flex,
  Icon,
  Input,
  FormControl,
  FormLabel,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { FiTrendingDown, FiTrendingUp, FiUsers, FiTarget } from 'react-icons/fi';
import AnalyticsApi from '../../../../api/analytics';
import CustomFunnelChart from './CustomFunnelChart';

const TransfersFunnelModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30); // Default to 30 days ago
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split('T')[0];
  });

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const chartBgColor = useColorModeValue('gray.50', 'gray.700');
  const chartTextColor = useColorModeValue('gray.800', 'white');

  useEffect(() => {
    if (isOpen) {
      fetchFunnelReport();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFunnelReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await AnalyticsApi.getTransfersFunnelReport(startDate, endDate);
      if (response.data) {
        setReportData(response.data);
      }
    } catch (err) {
      console.error('Error fetching transfers funnel report:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = () => {
    fetchFunnelReport();
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('he-IL').format(num);
  };

  const formatPercentage = (num) => {
    return `${num.toFixed(1)}%`;
  };

  const prepareFunnelData = (funnelSteps) => {
    return funnelSteps.map((step, index) => ({
      name: step.stepName,
      value: step.totalEvents,
      fill: getStepColor(index),
      stepNumber: step.stepNumber,
      conversionRate: step.conversionRate,
      dropOffRate: step.dropOffRate
    }));
  };

  const getStepColor = (index) => {
    const colors = ['#3182CE', '#38A169', '#D69E2E', '#E53E3E'];
    return colors[index] || '#718096';
  };

  // Removed getParameterBreakdownText since we're now using visual charts

  const prepareParameterChartData = (parameterBreakdown, parameterName) => {
    if (!parameterBreakdown || !parameterBreakdown[parameterName]) {
      return [];
    }

    const colors = ['#3182CE', '#38A169', '#D69E2E', '#E53E3E', '#9F7AEA', '#F56565'];

    return Object.entries(parameterBreakdown[parameterName]).map(([value, count], index) => ({
      name: getParameterDisplayName(value),
      value: count,
      fill: colors[index % colors.length],
      percentage: 0 // Will be calculated when rendering
    }));
  };

  const getParameterDisplayName = (value) => {
    const displayNames = {
      'home_view': 'מסך בית',
      'tracking_view': 'מסך מעקב',
      'shift_details_view': 'פרטי משמרת',
      'custom': 'סכום מותאם',
      'fixed': 'סכום קבוע'
    };
    return displayNames[value] || value;
  };

  const calculatePercentages = (data) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    return data.map(item => ({
      ...item,
      percentage: total > 0 ? ((item.value / total) * 100).toFixed(1) : 0
    }));
  };

  const CustomParameterTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <Box
          bg={bgColor}
          p={3}
          border="1px"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="lg"
        >
          <Text fontWeight="bold">{data.name}</Text>
          <Text>כמות: {formatNumber(data.value)}</Text>
          <Text>אחוז: {data.payload.percentage}%</Text>
        </Box>
      );
    }
    return null;
  };

  // CustomTooltip removed since we're using CustomFunnelChart with its own tooltips

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent bg={bgColor} maxH="90vh" dir="rtl">
        <ModalHeader
          fontSize="xl"
          fontWeight="bold"
          textAlign="center"
          borderBottom="1px"
          borderColor="gray.200"
          pb={4}
        >
          דוח משפך העברות כספיות
        </ModalHeader>
        <ModalCloseButton
          position="absolute"
          left="16px"
          right="auto"
          top="16px"
          zIndex="3"
          size="lg"
        />
        <ModalBody overflowY="auto" p={6}>
          {/* Date Range Controls */}
          <Box p={6} border="1px" borderColor={borderColor} borderRadius="lg" mb={6} bg="gray.50">
            <VStack spacing={6}>
              <Text fontSize="lg" fontWeight="bold" textAlign="center">בחירת טווח תאריכים</Text>
              <HStack spacing={6} w="100%" justify="center" flexWrap="wrap">
                <FormControl maxW="200px">
                  <FormLabel fontSize="sm" fontWeight="semibold" textAlign="right">תאריך התחלה</FormLabel>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    dir="ltr"
                    textAlign="center"
                  />
                </FormControl>
                <FormControl maxW="200px">
                  <FormLabel fontSize="sm" fontWeight="semibold" textAlign="right">תאריך סיום</FormLabel>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    dir="ltr"
                    textAlign="center"
                  />
                </FormControl>
                <Button
                  colorScheme="blue"
                  onClick={handleDateChange}
                  mt={6}
                  size="md"
                  px={8}
                >
                  עדכן דוח
                </Button>
              </HStack>
            </VStack>
          </Box>

          {loading ? (
            <VStack spacing={4} py={8}>
              <Spinner size="lg" />
              <Text>Loading Transfers Funnel Report...</Text>
            </VStack>
          ) : error ? (
            <Alert status="error">
              <AlertIcon />
              <AlertTitle>שגיאה!</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : reportData ? (
            <VStack spacing={6} align="stretch">
              {/* Summary Stats */}
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                <Stat p={6} border="1px" borderColor={borderColor} borderRadius="lg" bg="white" boxShadow="sm">
                  <StatLabel>
                    <Flex align="center" justify="center">
                      <Text>התחלות העברה</Text>
                      <Icon as={FiUsers} ml={3} color="blue.500" />
                    </Flex>
                  </StatLabel>
                  <StatNumber color="blue.500" textAlign="center" fontSize="2xl">
                    {formatNumber(reportData.funnelSteps[0]?.totalEvents || 0)}
                  </StatNumber>
                  <StatHelpText textAlign="center">משתמשים שהתחילו תהליך</StatHelpText>
                </Stat>

                <Stat p={6} border="1px" borderColor={borderColor} borderRadius="lg" bg="white" boxShadow="sm">
                  <StatLabel>
                    <Flex align="center" justify="center">
                      <Text>העברות שהושלמו</Text>
                      <Icon as={FiTarget} ml={3} color="green.500" />
                    </Flex>
                  </StatLabel>
                  <StatNumber color="green.500" textAlign="center" fontSize="2xl">
                    {formatNumber(reportData.funnelSteps[3]?.totalEvents || 0)}
                  </StatNumber>
                  <StatHelpText textAlign="center">העברות שהסתיימו בהצלחה</StatHelpText>
                </Stat>

                <Stat p={6} border="1px" borderColor={borderColor} borderRadius="lg" bg="white" boxShadow="sm">
                  <StatLabel>
                    <Flex align="center" justify="center">
                      <Text>שיעור המרה כולל</Text>
                      <Icon as={FiTrendingUp} ml={3} color="purple.500" />
                    </Flex>
                  </StatLabel>
                  <StatNumber color="purple.500" textAlign="center" fontSize="2xl">
                    {formatPercentage(reportData.overallConversionRate)}
                  </StatNumber>
                  <StatHelpText textAlign="center">מהתחלה לסיום</StatHelpText>
                </Stat>

                <Stat p={6} border="1px" borderColor={borderColor} borderRadius="lg" bg="white" boxShadow="sm">
                  <StatLabel textAlign="center">
                    <Text>טווח תאריכים</Text>
                  </StatLabel>
                  <StatNumber fontSize="lg" textAlign="center" color="gray.700">
                    {reportData.dateRange}
                  </StatNumber>
                  <StatHelpText textAlign="center">נתונים מ-Google Analytics</StatHelpText>
                </Stat>
              </SimpleGrid>

              <Divider />

              {/* Tabbed Content */}
              <Tabs variant="enclosed" colorScheme="blue" dir="rtl">
                <TabList justifyContent="center" borderBottom="2px" borderColor="gray.200">
                  <Tab
                    _selected={{
                      color: "blue.600",
                      borderColor: "blue.600",
                      borderBottomColor: "white",
                      bg: "white"
                    }}
                    px={8}
                    py={4}
                  >
                    <Flex align="center">
                      <Text>משפך ויזואלי</Text>
                      <Icon as={FiTarget} ml={3} />
                    </Flex>
                  </Tab>
                  <Tab
                    _selected={{
                      color: "blue.600",
                      borderColor: "blue.600",
                      borderBottomColor: "white",
                      bg: "white"
                    }}
                    px={8}
                    py={4}
                  >
                    <Flex align="center">
                      <Text>פירוט שלבים</Text>
                      <Icon as={FiTrendingDown} ml={3} />
                    </Flex>
                  </Tab>
                </TabList>

                <TabPanels>
                  {/* Funnel Visualization Tab */}
                  <TabPanel>
                    <Box p={4} border="1px" borderColor={borderColor} borderRadius="lg" bg={chartBgColor}>
                      <VStack spacing={4}>
                        <Text fontSize="xl" fontWeight="bold" textAlign="center" color={chartTextColor}>
                          Transfers Funnel 
                        </Text>
                        <Text fontSize="sm" color="gray.500" textAlign="center">
                          ויזואליזציה של תהליך ההעברה מהתחלה ועד סיום
                        </Text>
                        <CustomFunnelChart
                          data={prepareFunnelData(reportData.funnelSteps)}
                          onStepClick={(step) => {
                            console.log('Clicked step:', step);
                          }}
                        />
                      </VStack>
                    </Box>
                  </TabPanel>

                  {/* Detailed Steps Tab */}
                  <TabPanel p={6}>
                    <VStack spacing={8} align="stretch">
                      <Text fontSize="xl" fontWeight="bold" textAlign="center" color="gray.700">
                        פירוט מפורט של שלבי המשפך
                      </Text>
                      {reportData.funnelSteps.map((step, index) => (
                        <Box key={step.stepNumber} p={8} border="2px" borderColor={borderColor} borderRadius="xl" bg="white" boxShadow="md">
                          <VStack align="stretch" spacing={6}>
                            <HStack justify="space-between" align="center">
                              <VStack align="end" spacing={2}>
                                <Badge colorScheme="blue" fontSize="md" px={4} py={1} borderRadius="full">
                                  שלב {step.stepNumber}
                                </Badge>
                                <Text fontSize="xl" fontWeight="bold" textAlign="right">
                                  {step.stepName}
                                </Text>
                                <Text fontSize="sm" color="gray.500" textAlign="right">
                                  {step.eventName}
                                </Text>
                              </VStack>
                              <VStack align="center" spacing={1}>
                                <Text fontSize="3xl" fontWeight="bold" color={getStepColor(index)}>
                                  {formatNumber(step.totalEvents)}
                                </Text>
                                <Text fontSize="sm" color="gray.600" fontWeight="semibold">
                                  אירועים
                                </Text>
                              </VStack>
                            </HStack>

                            <Divider borderColor="gray.300" />

                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                              <Stat textAlign="center" p={4} border="1px" borderColor="green.200" borderRadius="lg" bg="green.50">
                                <StatLabel fontSize="sm" fontWeight="semibold" color="green.700">שיעור המרה</StatLabel>
                                <StatNumber color="green.600" fontSize="2xl">
                                  {formatPercentage(step.conversionRate)}
                                </StatNumber>
                                <StatHelpText color="green.600">מהשלב הקודם</StatHelpText>
                              </Stat>
                              <Stat textAlign="center" p={4} border="1px" borderColor="red.200" borderRadius="lg" bg="red.50">
                                <StatLabel fontSize="sm" fontWeight="semibold" color="red.700">שיעור נשירה</StatLabel>
                                <StatNumber color="red.600" fontSize="2xl">
                                  {formatPercentage(step.dropOffRate)}
                                </StatNumber>
                                <StatHelpText color="red.600">מהשלב הקודם</StatHelpText>
                              </Stat>
                            </SimpleGrid>

                            {Object.keys(step.parameterBreakdown).length > 0 && (
                              <>
                                <Divider borderColor="gray.300" />
                                <Box>
                                  <Text fontSize="lg" fontWeight="bold" mb={6} textAlign="center" color="blue.600">
                                    פירוט פרמטרים
                                  </Text>

                                  {/* Parameter Distribution Charts */}
                                  <VStack spacing={8} align="stretch">
                                    {Object.entries(step.parameterBreakdown).map(([paramName, paramValues]) => {
                                      const chartData = calculatePercentages(
                                        prepareParameterChartData(step.parameterBreakdown, paramName)
                                      );

                                      return (
                                        <Box key={paramName} p={6} border="2px" borderColor="blue.200" borderRadius="xl" bg="blue.50">
                                          <VStack spacing={6}>
                                            <Text fontSize="lg" fontWeight="bold" textAlign="center" color="blue.700">
                                              {paramName === 'from' ? 'מקור ההתחלה' :
                                               paramName === 'origin' ? 'סוג הסכום' : paramName}
                                            </Text>

                                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} w="100%">
                                              {/* Pie Chart */}
                                              <Box bg="white" p={4} borderRadius="lg" border="1px" borderColor="gray.200">
                                                <Text fontSize="md" fontWeight="semibold" textAlign="center" mb={4} color="gray.700">
                                                  התפלגות באחוזים
                                                </Text>
                                                <Box height="250px">
                                                  <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                      <Pie
                                                        data={chartData}
                                                        cx="50%"
                                                        cy="50%"
                                                        outerRadius={80}
                                                        dataKey="value"
                                                        label={({ name, percentage }) => `${name}: ${percentage}%`}
                                                        labelLine={false}
                                                        fontSize={12}
                                                      >
                                                        {chartData.map((entry, index) => (
                                                          <Cell key={`cell-${index}`} fill={entry.fill} />
                                                        ))}
                                                      </Pie>
                                                      <Tooltip content={<CustomParameterTooltip />} />
                                                    </PieChart>
                                                  </ResponsiveContainer>
                                                </Box>
                                              </Box>

                                              {/* Bar Chart */}
                                              <Box bg="white" p={4} borderRadius="lg" border="1px" borderColor="gray.200">
                                                <Text fontSize="md" fontWeight="semibold" textAlign="center" mb={4} color="gray.700">
                                                  כמויות מוחלטות
                                                </Text>
                                                <Box height="250px">
                                                  <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                                                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                                      <XAxis
                                                        dataKey="name"
                                                        angle={-45}
                                                        textAnchor="end"
                                                        height={100}
                                                        fontSize={11}
                                                        fontWeight="500"
                                                      />
                                                      <YAxis fontSize={11} />
                                                      <Tooltip content={<CustomParameterTooltip />} />
                                                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                                                        {chartData.map((entry, index) => (
                                                          <Cell key={`cell-${index}`} fill={entry.fill} />
                                                        ))}
                                                      </Bar>
                                                    </BarChart>
                                                  </ResponsiveContainer>
                                                </Box>
                                              </Box>
                                            </SimpleGrid>

                                            {/* Summary Text */}
                                            <Box w="100%" p={4} bg="gray.100" borderRadius="md">
                                              <Text fontSize="sm" color="gray.700" textAlign="center" fontWeight="medium">
                                                {Object.entries(paramValues)
                                                  .map(([value, count]) => `${getParameterDisplayName(value)}: ${formatNumber(count)}`)
                                                  .join(' • ')}
                                              </Text>
                                            </Box>
                                          </VStack>
                                        </Box>
                                      );
                                    })}
                                  </VStack>
                                </Box>
                              </>
                            )}
                          </VStack>
                        </Box>
                      ))}
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </VStack>
          ) : (
            <Text textAlign="center" py={12} fontSize="lg" color="gray.500">
              אין נתונים להצגה
            </Text>
          )}
        </ModalBody>
        <ModalFooter borderTop="1px" borderColor="gray.200" justifyContent="center">
          <Button
            onClick={onClose}
            colorScheme="gray"
            size="lg"
            px={8}
            fontWeight="semibold"
          >
            סגור
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TransfersFunnelModal;
